/**
 * Video export via ffmpeg.wasm — the replacement for the prototype's
 * MediaRecorder/captureStream pipeline (spec step 2).
 *
 * Why this design:
 * - Nothing is captured live. Frames are rendered offline from a Timeline
 *   with `drawScene`, so output never depends on the browser's compositor,
 *   which is exactly what failed on iOS Safari before.
 * - H.264 in MP4 (libx264, yuv420p, faststart) is what Reels/TikTok/CapCut
 *   and the iOS Photos app accept. `ultrafast` keeps single-thread wasm
 *   encoding tolerable on a phone and yields a baseline-compatible stream.
 * - Frames are encoded in fixed-size chunks and deleted as we go, so the
 *   wasm heap stays flat no matter how long the clip is. Chunks are then
 *   stream-copied together. That bounds memory on iPhone.
 * - The single-thread core is served same-origin from public/ffmpeg. No
 *   SharedArrayBuffer, so no COOP/COEP headers; no CDN, so no third-party
 *   requests.
 */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { createFrameRenderer, EXPORT_FPS, frameCount, type SceneBase } from './frames';
import { ExportCancelledError, type ExportProgress, type ExportResult, type ExportSegment } from './types';

/** Frames per encoding chunk. ~120 JPEGs at 720x1280 is a few MB in the wasm FS. */
export const CHUNK_FRAMES = 120;
const OUTPUT = 'out.mp4';
const LIST = 'list.txt';
const LOG_TAIL = 40;
/** Rendering a frame is cheap next to encoding it; weights the progress bar. */
const RENDER_WEIGHT = 1;
const ENCODE_WEIGHT = 4;

const CORE_BASE = new URL(`${import.meta.env.BASE_URL}ffmpeg/${__FFMPEG_CORE_VERSION__}/`, location.href).href;
export const CORE_URL = CORE_BASE + 'ffmpeg-core.js';
export const MANIFEST_URL = CORE_BASE + 'manifest.json';

/** Written by scripts/copy-ffmpeg-core.mjs next to the core files. */
interface CoreManifest {
  version: string;
  wasmBytes: number;
  wasmParts: string[];
}

export type DownloadProgress = (loadedBytes: number, totalBytes: number) => void;

/**
 * Download the wasm core. It is stored as parts under 25 MiB (static hosts
 * cap file sizes) and reassembled here into one Blob. Progress is reported
 * across all parts so the UI can show a percentage for the 31 MB first load.
 */
async function fetchCoreWasm(onProgress?: DownloadProgress): Promise<Blob> {
  const manifestRes = await fetch(MANIFEST_URL);
  if (!manifestRes.ok) throw new Error(`Encoder manifest missing: HTTP ${manifestRes.status}`);
  const manifest = (await manifestRes.json()) as CoreManifest;
  const loaded = new Array<number>(manifest.wasmParts.length).fill(0);
  const report = (): void => onProgress?.(loaded.reduce((a, b) => a + b, 0), manifest.wasmBytes);

  const partChunks = await Promise.all(
    manifest.wasmParts.map(async (name, i): Promise<BlobPart[]> => {
      const res = await fetch(CORE_BASE + name);
      if (!res.ok) throw new Error(`Encoder download failed for ${name}: HTTP ${res.status}`);
      if (!res.body) {
        const buf = await res.arrayBuffer();
        loaded[i] = buf.byteLength;
        report();
        return [buf];
      }
      const reader = res.body.getReader();
      const chunks: BlobPart[] = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded[i] = (loaded[i] ?? 0) + value.byteLength;
        report();
      }
      return chunks;
    }),
  );
  const blob = new Blob(partChunks.flat(), { type: 'application/wasm' });
  if (blob.size !== manifest.wasmBytes) throw new Error(`Encoder download incomplete: ${blob.size} of ${manifest.wasmBytes} bytes`);
  return blob;
}

export interface ExportOptions {
  /** One segment per angle. Almost always one; several for a multi-angle export. */
  segments: ExportSegment<SceneBase>[];
  width: number;
  height: number;
  fps?: number;
  signal?: AbortSignal;
  onProgress?: (p: ExportProgress) => void;
}

const frameName = (i: number): string => `f${String(i).padStart(5, '0')}.jpg`;
/**
 * ffmpeg output options that keep the MP4 free of metadata (see export()).
 * `-map_metadata -1` drops anything inherited from inputs; the two bitexact
 * flags remove the container's encoder/version tag and any time-dependent
 * fields. ffmpeg also writes its own name ("Lavc libx264") into the sample
 * description's compressor-name field unless the stream already carries an
 * `encoder` tag, and an empty value deletes the tag rather than blanking it,
 * so the field is given the conventional generic label "H.264" instead.
 */
const NO_METADATA = [
  '-map_metadata', '-1',
  '-fflags', '+bitexact',
  '-flags', '+bitexact',
  '-metadata:s:v:0', 'encoder=H.264',
] as const;
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

export class FfmpegExporter {
  private ffmpeg: FFmpeg | null = null;
  private pending: FFmpeg | null = null;
  private loading: Promise<FFmpeg> | null = null;
  private downloadListeners: DownloadProgress[] = [];
  private logTail: string[] = [];
  private busy = false;

  get ready(): boolean {
    return this.ffmpeg !== null;
  }

  /** Recent ffmpeg log lines, for error reports on devices with no console. */
  get recentLog(): string {
    return this.logTail.slice(-10).join('\n');
  }

  /**
   * Load the core (one-time ~31 MB download, then browser-cached). Idempotent;
   * concurrent callers share one load and all receive download progress.
   */
  preload(onDownload?: DownloadProgress): Promise<FFmpeg> {
    if (this.ffmpeg) return Promise.resolve(this.ffmpeg);
    if (onDownload) this.downloadListeners.push(onDownload);
    if (!this.loading) {
      this.loading = this.doLoad()
        .catch((err: unknown) => {
          this.loading = null;
          this.pending = null;
          throw err;
        })
        .finally(() => {
          this.downloadListeners = [];
        });
    }
    return this.loading;
  }

  private async doLoad(): Promise<FFmpeg> {
    const ff = new FFmpeg();
    this.pending = ff;
    ff.on('log', ({ message }) => {
      this.logTail.push(message);
      if (this.logTail.length > LOG_TAIL) this.logTail.shift();
      if (import.meta.env.DEV) console.debug('[ffmpeg]', message);
    });
    const wasmBlob = await fetchCoreWasm((loaded, total) => {
      for (const l of this.downloadListeners) l(loaded, total);
    });
    // The worker fetches the wasm through this blob: URL (same origin, in
    // memory). Revoke it once the core is instantiated so the copy can be
    // garbage-collected.
    const wasmURL = URL.createObjectURL(wasmBlob);
    try {
      await ff.load({ coreURL: CORE_URL, wasmURL });
    } finally {
      URL.revokeObjectURL(wasmURL);
    }
    this.ffmpeg = ff;
    this.pending = null;
    return ff;
  }

  /** Kill the worker. The next export reloads the core from cache. */
  reset(): void {
    const ff = this.ffmpeg ?? this.pending;
    ff?.terminate();
    this.ffmpeg = null;
    this.pending = null;
    this.loading = null;
    this.busy = false;
  }

  async export(opts: ExportOptions): Promise<ExportResult> {
    if (this.busy) throw new Error('An export is already running');
    if (opts.segments.length === 0) throw new Error('No angle has both photos yet');
    this.busy = true;
    const { segments: angleSegments, width, height, signal, onProgress } = opts;
    const fps = opts.fps ?? EXPORT_FPS;
    const frameMs = 1000 / fps;
    const framesPerAngle = angleSegments.map((seg) => frameCount(seg.timeline.durationMs, fps));
    const total = framesPerAngle.reduce((a, b) => a + b, 0);

    let rendered = 0;
    let encoded = 0;
    let encodingNow = 0;
    let chunkSize = 0;
    const report = (phase: ExportProgress['phase']): void => {
      const fraction = clamp01(
        (rendered * RENDER_WEIGHT + (encoded + encodingNow) * ENCODE_WEIGHT) / (total * (RENDER_WEIGHT + ENCODE_WEIGHT)),
      );
      onProgress?.({ phase, fraction, rendered, encoded: Math.min(total, Math.round(encoded + encodingNow)), total });
    };
    const throwIfAborted = (): void => {
      if (signal?.aborted) throw new ExportCancelledError();
    };
    const onFfProgress = ({ progress }: { progress: number }): void => {
      encodingNow = clamp01(progress) * chunkSize;
      report('encoding');
    };

    let ff: FFmpeg | null = null;
    const outputPieces: string[] = [];
    try {
      report('loading');
      ff = await this.preload((loaded, totalBytes) => {
        onProgress?.({ phase: 'loading', fraction: totalBytes ? clamp01(loaded / totalBytes) : 0, rendered: 0, encoded: 0, total });
      });
      throwIfAborted();
      ff.on('progress', onFfProgress);

      // Every angle's frames become one or more encoded chunk files here;
      // multiple angles just mean more chunk files, all concatenated once at
      // the end. A single-angle, single-chunk export skips concat entirely.
      let pieceIndex = 0;
      for (let a = 0; a < angleSegments.length; a++) {
        const { scene, timeline } = angleSegments[a]!;
        const angleFrames = framesPerAngle[a]!;
        const chunksInAngle = Math.ceil(angleFrames / CHUNK_FRAMES);
        const renderer = createFrameRenderer(width, height, scene);
        try {
          for (let c = 0; c < chunksInAngle; c++) {
            const first = c * CHUNK_FRAMES;
            chunkSize = Math.min(CHUNK_FRAMES, angleFrames - first);

            for (let i = 0; i < chunkSize; i++) {
              throwIfAborted();
              const bytes = await renderer.renderJpeg(timeline.xAt((first + i) * frameMs));
              await ff.writeFile(frameName(i), bytes);
              rendered++;
              report('rendering');
            }

            throwIfAborted();
            encodingNow = 0;
            report('encoding');
            const isOnlyPiece = angleSegments.length === 1 && chunksInAngle === 1;
            const out = isOnlyPiece ? OUTPUT : `seg${String(pieceIndex).padStart(3, '0')}.mp4`;
            await this.run(
              ff,
              [
                '-framerate', String(fps),
                '-start_number', '0',
                '-i', 'f%05d.jpg',
                '-frames:v', String(chunkSize),
                '-an',
                // No metadata of any kind in the output: nothing inherited from
                // inputs, no encoder/version tags, no build- or time-dependent
                // fields. The photos' EXIF (GPS, device, timestamps) was already
                // discarded when they were decoded to pixels; this makes the
                // file side of that guarantee explicit and testable.
                ...NO_METADATA,
                // JPEG intermediates are BT.601 full-range. Convert to BT.709
                // limited-range and flag it, which is what every phone player
                // and social platform assumes for HD video. Without this, reds
                // shift visibly on playback.
                '-vf', 'scale=in_range=pc:in_color_matrix=bt601:out_range=tv:out_color_matrix=bt709',
                '-color_range', 'tv',
                '-colorspace', 'bt709',
                '-color_primaries', 'bt709',
                '-color_trc', 'bt709',
                '-c:v', 'libx264',
                '-preset', 'ultrafast',
                '-crf', '20',
                '-pix_fmt', 'yuv420p',
                '-r', String(fps),
                ...(isOnlyPiece ? ['-movflags', '+faststart'] : []),
                out,
              ],
              signal,
            );
            encoded += chunkSize;
            encodingNow = 0;
            outputPieces.push(out);
            for (let i = 0; i < chunkSize; i++) await ff.deleteFile(frameName(i));
            pieceIndex++;
          }
        } finally {
          renderer.dispose();
        }
      }

      if (outputPieces.length > 1) {
        report('finalizing');
        const list = outputPieces.map((s) => `file '${s}'`).join('\n') + '\n';
        await ff.writeFile(LIST, new TextEncoder().encode(list));
        await this.run(ff, ['-f', 'concat', '-safe', '0', '-i', LIST, '-c', 'copy', ...NO_METADATA, '-movflags', '+faststart', OUTPUT], signal);
        await ff.deleteFile(LIST);
        for (const s of outputPieces) await ff.deleteFile(s);
      }

      const data = await ff.readFile(OUTPUT, 'binary');
      await ff.deleteFile(OUTPUT);
      const raw = data instanceof Uint8Array ? data : new TextEncoder().encode(data);
      if (raw.byteLength === 0) throw new Error('ffmpeg produced an empty file');
      // Copy into a fresh, non-shared ArrayBuffer so the Blob owns its bytes.
      const bytes = new Uint8Array(raw.byteLength);
      bytes.set(raw);
      return { blob: new Blob([bytes], { type: 'video/mp4' }), mimeType: 'video/mp4', ext: 'mp4' };
    } catch (err) {
      // Any failure or cancel: tear the worker down. That frees the wasm heap
      // and guarantees a clean virtual filesystem for the next attempt.
      this.reset();
      if (isAbortError(err) || signal?.aborted) throw new ExportCancelledError();
      throw err;
    } finally {
      ff?.off('progress', onFfProgress);
      this.busy = false;
    }
  }

  private async run(ff: FFmpeg, args: string[], signal?: AbortSignal): Promise<void> {
    // @ffmpeg/ffmpeg prepends -nostdin -y itself.
    const code = await ff.exec(args, -1, { signal });
    if (code !== 0) {
      throw new Error(`ffmpeg exited with code ${code}\n${this.recentLog}`);
    }
  }
}
